/**
 * XLSX export with exceljs (MIT), loaded on demand (ADR 0018): one sheet per report table with a bold,
 * frozen header row and auto filter, typed cells with number formats (durations as [h]:mm, percentages,
 * dates as real Excel dates in the report time zone), plus an info sheet (filters, generated at/by).
 */
import { fill, formatDateTimeIn, isSystemText, wallClock, yieldNow, type Cell, type CellType, type ExportMeta, type Sheet } from "./sheet";

type ExcelModule = typeof import("exceljs");

async function loadExcel(): Promise<ExcelModule> {
  const mod = (await import("exceljs")) as unknown as ExcelModule & { default?: ExcelModule };
  return mod.default ?? mod;
}

/** Excel sheet names: ≤ 31 characters, none of []:*?/\ . */
export function sheetName(name: string, used: Set<string>): string {
  const base = name.replace(/[[\]:*?/\\]/g, " ").trim().slice(0, 31) || "Sheet";
  let out = base;
  for (let i = 2; used.has(out.toLowerCase()); i++) out = `${base.slice(0, 28)} ${i}`;
  used.add(out.toLowerCase());
  return out;
}

function numFmt(type: CellType, locale: ExportMeta["locale"]): string | undefined {
  switch (type) {
    case "int":
      return "0";
    case "dec1":
      return "0.0";
    case "dec2":
      return "0.00";
    case "coord":
      return "0.000000";
    case "percent":
      return "0.0%";
    case "duration":
      return "[h]:mm";
    case "datetime":
      return locale === "tr" ? "dd.mm.yyyy hh:mm" : "yyyy-mm-dd hh:mm";
    case "date":
      return locale === "tr" ? "dd.mm.yyyy" : "yyyy-mm-dd";
    default:
      return undefined;
  }
}

/** The value written to Excel for a typed cell. */
export function excelValue(v: Cell, type: CellType, meta: Pick<ExportMeta, "tz" | "labels">): string | number | Date | null {
  if (v === null || v === undefined || v === "") return null;
  if (isSystemText(v)) return v.system;
  switch (type) {
    case "percent":
      return typeof v === "number" ? v / 100 : null;
    case "duration":
      return typeof v === "number" ? v / 86400 : null;
    case "datetime": {
      // Excel dates have no zone: write the wall-clock time of the report zone.
      const w = typeof v === "string" ? wallClock(v, meta.tz) : null;
      return w ? new Date(Date.UTC(w.y, w.m - 1, w.d, w.hh, w.mm, w.ss)) : null;
    }
    case "date": {
      const m = typeof v === "string" ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(v) : null;
      return m ? new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))) : null;
    }
    case "bool":
      return v === true ? meta.labels.yes : v === false ? meta.labels.no : null;
    case "int":
    case "dec1":
    case "dec2":
    case "coord":
      return typeof v === "number" ? v : null;
    default:
      return String(v);
  }
}

export async function buildXlsx(sheets: Sheet[], meta: ExportMeta, chunk = 2000): Promise<ArrayBuffer> {
  const ExcelJS = await loadExcel();
  const wb = new ExcelJS.Workbook();
  wb.creator = "FTS";
  wb.created = new Date(meta.generatedAt);
  const used = new Set<string>();
  for (const s of sheets) {
    const ws = wb.addWorksheet(sheetName(s.name, used), { views: [{ state: "frozen", ySplit: 1 }] });
    ws.columns = s.columns.map((c, i) => ({
      key: `c${i}`,
      header: c.header,
      width: Math.min(60, Math.max(c.width ?? 12, c.header.length + 2)),
      style: numFmt(c.type, meta.locale) ? { numFmt: numFmt(c.type, meta.locale) } : {},
    }));
    const header = ws.getRow(1);
    header.font = { bold: true };
    header.alignment = { vertical: "middle" };
    for (let i = 0; i < s.rows.length; i++) {
      const row = s.rows[i]!;
      ws.addRow(s.columns.map((c, j) => excelValue(row[j] ?? null, c.type, meta)));
      if (chunk > 0 && i > 0 && i % chunk === 0) await yieldNow();
    }
    if (s.columns.length > 0) ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: s.columns.length } };
  }
  const info = wb.addWorksheet(sheetName(meta.labels.info, used));
  info.columns = [{ width: 28 }, { width: 70 }];
  info.addRow([`FTS — ${meta.reportName}`]).font = { bold: true, size: 13 };
  info.addRow([]);
  for (const [k, v] of meta.filters) info.addRow([k, v]);
  info.addRow([meta.labels.timeZone, meta.tz]);
  info.addRow([meta.labels.generatedAt, formatDateTimeIn(meta.generatedAt, meta.tz, meta.locale)]);
  info.addRow([meta.labels.generatedBy, meta.generatedBy]);
  if (sheets.every((s) => s.rows.length === 0)) info.addRow([fill(meta.labels.noRows, {})]);
  const buf = await wb.xlsx.writeBuffer();
  return buf instanceof ArrayBuffer ? buf : (buf as Uint8Array).buffer.slice((buf as Uint8Array).byteOffset, (buf as Uint8Array).byteOffset + (buf as Uint8Array).byteLength) as ArrayBuffer;
}
